import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Volume2, VolumeX, Check } from "lucide-react";
import { Reveal, MaskedLines } from "./AnimatedSection";

// PLACEHOLDER showreel footage — replace the files in /public/assets/
// (showreel.webm / showreel.mp4 / showreel-poster.jpg) with your own 9:16 brand film.
const VIDEO_WEBM = "/assets/websitesocialwavez.mp4";
const VIDEO_MP4 = "/assets/showreel.mp4";
const VIDEO_POSTER = "/assets/showreel-poster.jpg";

const POINTS = [
  "Interfaces designed around your workflow",
  "Logic and data wired to talk to each other",
  "Motion with intent — never decoration for its own sake",
];

export const ShowreelFrame = ({
  eager = false,
  className = "",
  showCaption = false,
  idPrefix = "home-video",
}) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const near = useInView(wrapRef, { margin: "400px" });
  const [load, setLoad] = useState(eager);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (near) setLoad(true);
  }, [near]);

  useEffect(() => {
    if (load && videoRef.current) videoRef.current.play().catch(() => {});
  }, [load]);

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) {
      videoRef.current.muted = next;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div ref={wrapRef} className={`relative mx-auto w-full ${className}`}>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[110%] w-[130%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1C7543]/20 blur-[100px]" />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        <div className="rounded-[2.4rem] bg-gradient-to-br from-[#3CBE6E] via-[#1C7543] to-[#D3B74D] p-[2px] shadow-[0_40px_110px_rgba(14,58,40,0.4)]">
          <div className="relative aspect-[9/16] overflow-hidden rounded-[2.3rem] bg-[#07130C]">
            {load && (
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={VIDEO_POSTER}
                data-testid={idPrefix}
                aria-label="Showreel: code and interfaces in motion"
                className="h-full w-full object-cover"
              >
                <source src={VIDEO_WEBM} type="video/webm" />
                {/* <source src={VIDEO_MP4} type="video/mp4" /> */}
              </video>
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07130C]/50 via-transparent to-[#07130C]/20" />
            <button
              type="button"
              onClick={toggleSound}
              data-testid={`${idPrefix}-sound-toggle`}
              aria-label={muted ? "Unmute showreel" : "Mute showreel"}
              className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-[#07130C]/60 text-white backdrop-blur-md transition-colors duration-200 hover:bg-[#07130C]/85"
            >
              {muted ? <VolumeX className="h-[18px] w-[18px]" /> : <Volume2 className="h-[18px] w-[18px]" />}
            </button>
            <span className="absolute left-4 top-4 rounded-full bg-[#07130C]/60 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85 backdrop-blur-md">
              Showreel
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const VideoShowcase = () => {
  return (
    <section className="relative overflow-hidden bg-[#F8FBF6] py-28 md:py-36" aria-label="Showreel">
      <div className="bg-grid-light absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#07130C]/50">
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
              In Motion
            </div>
          </Reveal>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[#07130C] md:text-5xl">
            <MaskedLines
              lines={[
                "This Is What",
                <span key="v2" className="text-gradient-aurora">Connected</span>,
                "Looks Like.",
              ]}
            />
          </h2>
          <Reveal delay={0.25}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#07130C]/60 md:text-lg">
              Code, interfaces and data — moving as one system. Every build we
              ship is crafted to feel this alive: fast, precise and quietly
              powerful.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <ul className="mt-8 space-y-3">
              {POINTS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm font-medium text-[#07130C]/70">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#1C7543] to-[#D3B74D] text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.45}>
            <Link
              to="/project"
              data-testid="video-showcase-cta"
              className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-[#1C7543] transition-colors hover:text-[#D3B74D]"
            >
              See the systems we've built
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <ShowreelFrame className="max-w-[320px] md:max-w-[360px]" showCaption />
      </div>
    </section>
  );
};
